// The club plan for one group: is the group's stand board unlocked, and connecting a member's club plan to a group.
// The server (server/billing.ts) keeps only "this group ↔ this paying member's purchase id"; any member can ask with
// the group's token, so a plan bought by one member shows on every member's phone. The answer is kept per group,
// so the board works offline as it last did.
import type { Group } from "../../shared/model.ts";
import { appUserId, billingOff, syncServer } from "../billing.ts";
import { lsJson, lsSet } from "../util.ts";

export interface ClubStatus { active: boolean; expiresAt: number | null; mine: boolean }
const cache: Record<string, ClubStatus> = lsJson("clubStatus", {});
const save = () => lsSet("clubStatus", JSON.stringify(cache));

/** The board's extras (more stands, the draw) are open for this group. */
export const clubOpen = (g: Group): boolean => billingOff() || !!cache[g.id]?.active;
/** You are the member whose plan unlocks this group. */
export const clubMine = (g: Group): boolean => !!cache[g.id]?.mine;
export const clubStatus = (g: Group): ClubStatus | null => cache[g.id] ?? null;

async function api(g: Group, method: "GET" | "POST", body?: object): Promise<Response> {
  const id = await appUserId();
  return fetch(`/api/club?g=${g.id}`, { method, signal: AbortSignal.timeout(20000),
    headers: { "x-group-token": g.token, ...(id ? { "x-app-user": id } : {}), ...(body ? { "content-type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined });
}

/** Asks the server whether the group's board is unlocked. */
export async function refreshClub(g: Group): Promise<ClubStatus | null> {
  if (billingOff() || !navigator.onLine) return clubStatus(g);
  try {
    const res = await api(g, "GET");
    if (!res.ok) return clubStatus(g);
    cache[g.id] = (await res.json()) as ClubStatus;
    save();
  } catch { /* offline: keep what we had */ }
  return clubStatus(g);
}

/** Connects your club plan to this group (moving it from another group if it was there). */
export async function claimClub(g: Group): Promise<"ok" | "already" | "noplan" | "failed"> {
  try {
    await syncServer(); // the server re-reads the purchase first
    const res = await api(g, "POST", { appUserId: await appUserId() });
    if (res.status === 409) return "already";
    if (res.status === 402) return "noplan";
    if (!res.ok) return "failed";
    cache[g.id] = (await res.json()) as ClubStatus;
    // the plan covers one group: forget "mine" elsewhere until those groups are checked again
    for (const [id, s] of Object.entries(cache)) if (id !== g.id && s.mine) cache[id] = { ...s, active: false, mine: false };
    save();
    return "ok";
  } catch { return "failed"; }
}
